import { create } from 'zustand';
import { LayoutDirection } from '../utils/diagramKits';
import { AppSettings, ThemeMode, IconStyle, ColorPickerStyle, SocketLabelVisibility, EdgeRoutingMode, ThemePalette } from '../types';

// ── Theme palettes ──────────────────────────────────────────────────────
const DARK_PALETTE: ThemePalette = {
    canvasBg: '#0d1117',
    gridLine: '#161b27',
    nodeBody: '#161c2d',
    nodeStroke: '#252d40',
    nodeSelectedStroke: '',
    nodeText: '#dde3f0',
    nodeSubText: '#94a3b8',
    nodeAccentBar: '',
    nodeTypeLabel: '',
    nodeHighlight: 'rgba(255,255,255,0.06)',
    nodeShadow: 'rgba(0,0,0,0.4)',
    edgeDefault: '#3d4d6a',
    edgeSelected: '#818cf8',
    edgeArrow: '#4b5e80',
    socketIdle: '#2a3450',
    socketStroke: '#3d4d6a',
    panelBg: 'rgba(22,27,42,0.95)',
    panelBorder: '#2d3452',
    panelText: '#e2e8f0',
    panelSubText: '#94a3b8',
    groupFrameBg: 'rgba(100,116,139,0.08)',
    groupFrameBorder: '#64748b',
    groupLabelText: '#94a3b8',
};

const LIGHT_PALETTE: ThemePalette = {
    canvasBg: '#f0f2f5',
    gridLine: '#e2e5ea',
    nodeBody: '#ffffff',
    nodeStroke: '#d1d5db',
    nodeSelectedStroke: '',
    nodeText: '#1e293b',
    nodeSubText: '#64748b',
    nodeAccentBar: '',
    nodeTypeLabel: '',
    nodeHighlight: 'rgba(0,0,0,0.04)',
    nodeShadow: 'rgba(0,0,0,0.12)',
    edgeDefault: '#94a3b8',
    edgeSelected: '#6366f1',
    edgeArrow: '#64748b',
    socketIdle: '#cbd5e1',
    socketStroke: '#94a3b8',
    panelBg: 'rgba(255,255,255,0.95)',
    panelBorder: '#d1d5db',
    panelText: '#1e293b',
    panelSubText: '#64748b',
    groupFrameBg: 'rgba(100,116,139,0.06)',
    groupFrameBorder: '#94a3b8',
    groupLabelText: '#475569',
};

// ── localStorage helpers ────────────────────────────────────────────────
const STORAGE_KEY = 'velostruct-settings';

function loadFromStorage(): Partial<AppSettings> {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return {};
        const stored = JSON.parse(raw) as Partial<AppSettings> & { tintEdgesByFrame?: boolean };
        // Destination tinting was once frame-only and named for it. Someone who
        // turned it off then meant off, so honour the old key before dropping it.
        if (stored.tintEdgesByDestination === undefined && stored.tintEdgesByFrame !== undefined) {
            stored.tintEdgesByDestination = stored.tintEdgesByFrame;
        }
        delete stored.tintEdgesByFrame;
        return stored;
    } catch {
        return {};
    }
}

function saveToStorage(settings: AppSettings) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
}

// ── Defaults ────────────────────────────────────────────────────────────
const DEFAULT_SETTINGS: AppSettings = {
    theme: 'dark',
    iconStyle: 'badge',
    colorPickerStyle: 'wheel',
    autoOpenInfoOnHover: true,
    socketLabelVisibility: 'always',
    defaultRoutingMode: 'bezier',
    forceLayoutEdges: false,
    tintEdgesByDestination: true,
    layoutDirection: 'auto',
    mermaidSubgraphMode: 'frames',
    mermaidNodeStyle: 'nodes',
    generalShapesEnabled: false,
    stickyDrawColorEnabled: false,
    lastDrawColor: null,
    sequenceMessageNumbering: true,
    sequenceMonochromeViewer: false,
    sequenceMonochromePaper: 'white',
};

// ── Store ───────────────────────────────────────────────────────────────
interface SettingsStore extends AppSettings {
    setTheme: (theme: ThemeMode) => void;
    setIconStyle: (style: IconStyle) => void;
    setColorPickerStyle: (style: ColorPickerStyle) => void;
    setAutoOpenInfoOnHover: (enabled: boolean) => void;
    setSocketLabelVisibility: (vis: SocketLabelVisibility) => void;
    setDefaultRoutingMode: (mode: EdgeRoutingMode) => void;
    setForceLayoutEdges: (enabled: boolean) => void;
    setTintEdgesByDestination: (enabled: boolean) => void;
    setLayoutDirection: (direction: LayoutDirection) => void;
    setMermaidSubgraphMode: (mode: 'frames' | 'groups') => void;
    setMermaidNodeStyle: (style: 'nodes' | 'shapes') => void;
    setGeneralShapesEnabled: (enabled: boolean) => void;
    setStickyDrawColorEnabled: (enabled: boolean) => void;
    setLastDrawColor: (color: string | null) => void;
    setSequenceMessageNumbering: (enabled: boolean) => void;
    setSequenceMonochromeViewer: (enabled: boolean) => void;
    setSequenceMonochromePaper: (paper: 'white' | 'black') => void;
    exportSettings: () => string;
    importSettings: (json: string) => void;
    palette: ThemePalette;
}

function makePalette(theme: ThemeMode): ThemePalette {
    return theme === 'dark' ? { ...DARK_PALETTE } : { ...LIGHT_PALETTE };
}

const persisted = loadFromStorage();
const initialSettings: AppSettings = { ...DEFAULT_SETTINGS, ...persisted };

function collectSettings(store: SettingsStore): AppSettings {
    return {
        theme: store.theme,
        iconStyle: store.iconStyle,
        colorPickerStyle: store.colorPickerStyle,
        autoOpenInfoOnHover: store.autoOpenInfoOnHover,
        socketLabelVisibility: store.socketLabelVisibility,
        defaultRoutingMode: store.defaultRoutingMode,
        forceLayoutEdges: store.forceLayoutEdges,
        tintEdgesByDestination: store.tintEdgesByDestination,
    layoutDirection: store.layoutDirection,
        mermaidSubgraphMode: store.mermaidSubgraphMode ?? 'frames',
        mermaidNodeStyle: store.mermaidNodeStyle === 'shapes' ? 'shapes' : 'nodes',
        generalShapesEnabled: store.generalShapesEnabled,
        stickyDrawColorEnabled: store.stickyDrawColorEnabled,
        lastDrawColor: store.lastDrawColor,
        sequenceMessageNumbering: store.sequenceMessageNumbering,
        sequenceMonochromeViewer: store.sequenceMonochromeViewer,
        sequenceMonochromePaper: store.sequenceMonochromePaper,
    };
}

export const useSettingsStore = create<SettingsStore>((set, get) => ({
    ...initialSettings,
    palette: makePalette(initialSettings.theme),

    setTheme: (theme: ThemeMode) =>
        set(() => {
            saveToStorage({ ...collectSettings(get()), theme });
            return { theme, palette: makePalette(theme) };
        }),

    setIconStyle: (iconStyle: IconStyle) =>
        set(() => {
            saveToStorage({ ...collectSettings(get()), iconStyle });
            return { iconStyle };
        }),

    setColorPickerStyle: (colorPickerStyle: ColorPickerStyle) =>
        set(() => {
            saveToStorage({ ...collectSettings(get()), colorPickerStyle });
            return { colorPickerStyle };
        }),

    setAutoOpenInfoOnHover: (autoOpenInfoOnHover: boolean) =>
        set(() => {
            saveToStorage({ ...collectSettings(get()), autoOpenInfoOnHover });
            return { autoOpenInfoOnHover };
        }),

    setSocketLabelVisibility: (socketLabelVisibility: SocketLabelVisibility) =>
        set(() => {
            saveToStorage({ ...collectSettings(get()), socketLabelVisibility });
            return { socketLabelVisibility };
        }),

    setDefaultRoutingMode: (defaultRoutingMode: EdgeRoutingMode) =>
        set(() => {
            saveToStorage({ ...collectSettings(get()), defaultRoutingMode });
            return { defaultRoutingMode };
        }),

    setForceLayoutEdges: (forceLayoutEdges: boolean) =>
        set(() => {
            saveToStorage({ ...collectSettings(get()), forceLayoutEdges });
            return { forceLayoutEdges };
        }),

    setTintEdgesByDestination: (tintEdgesByDestination: boolean) =>
        set(() => {
            saveToStorage({ ...collectSettings(get()), tintEdgesByDestination });
            return { tintEdgesByDestination };
        }),

    setLayoutDirection: (layoutDirection: LayoutDirection) =>
        set(() => {
            saveToStorage({ ...collectSettings(get()), layoutDirection });
            return { layoutDirection };
        }),

    setMermaidSubgraphMode: (mermaidSubgraphMode: 'frames' | 'groups') =>
        set(() => {
            saveToStorage({ ...collectSettings(get()), mermaidSubgraphMode });
            return { mermaidSubgraphMode };
        }),

    setMermaidNodeStyle: (mermaidNodeStyle: 'nodes' | 'shapes') =>
        set(() => {
            saveToStorage({ ...collectSettings(get()), mermaidNodeStyle });
            return { mermaidNodeStyle };
        }),

    setGeneralShapesEnabled: (generalShapesEnabled: boolean) =>
        set(() => {
            saveToStorage({ ...collectSettings(get()), generalShapesEnabled });
            return { generalShapesEnabled };
        }),

    setStickyDrawColorEnabled: (stickyDrawColorEnabled: boolean) =>
        set(() => {
            saveToStorage({ ...collectSettings(get()), stickyDrawColorEnabled });
            return { stickyDrawColorEnabled };
        }),

    setLastDrawColor: (lastDrawColor: string | null) =>
        set(() => {
            saveToStorage({ ...collectSettings(get()), lastDrawColor });
            return { lastDrawColor };
        }),

    setSequenceMessageNumbering: (sequenceMessageNumbering: boolean) =>
        set(() => {
            saveToStorage({ ...collectSettings(get()), sequenceMessageNumbering });
            return { sequenceMessageNumbering };
        }),

    setSequenceMonochromeViewer: (sequenceMonochromeViewer: boolean) =>
        set(() => {
            saveToStorage({ ...collectSettings(get()), sequenceMonochromeViewer });
            return { sequenceMonochromeViewer };
        }),

    setSequenceMonochromePaper: (sequenceMonochromePaper: 'white' | 'black') =>
        set(() => {
            saveToStorage({ ...collectSettings(get()), sequenceMonochromePaper });
            return { sequenceMonochromePaper };
        }),

    exportSettings: (): string => {
        return JSON.stringify(collectSettings(get()) satisfies AppSettings, null, 2);
    },

    importSettings: (json: string) => {
        try {
            const parsed = JSON.parse(json) as Partial<AppSettings>;
            const s: AppSettings = { ...DEFAULT_SETTINGS, ...parsed };
            saveToStorage(s);
            set({ ...s, palette: makePalette(s.theme) });
        } catch {
            console.warn('Failed to import settings');
        }
    },
}));

// Apply data-theme attribute on document root whenever theme changes
useSettingsStore.subscribe((state: SettingsStore) => {
    document.documentElement.setAttribute('data-theme', state.theme);
});
// Set on initial load
document.documentElement.setAttribute('data-theme', initialSettings.theme);
